/**
 * Login screen — shown when user is not authenticated.
 * If Okta is not configured, shows the admin setup wizard instead.
 */
import { login, checkOktaStatus } from '../services/auth';
import { authService } from '../services/api';

export async function renderLoginScreen(container: HTMLElement): Promise<void> {
  const status = await checkOktaStatus();

  if (status.setupRequired) {
    renderSetupWizard(container);
    return;
  }

  container.innerHTML = `
    <div style="display:flex;align-items:flex-start;justify-content:center;height:100vh;background:var(--colorBg)">
      <div style="text-align:center;max-width:400px;padding:40px;margin-top:20vh">
        <h1 style="font-size:24px;font-weight:700;color:var(--colorTextHeading);margin-bottom:8px">Use Case Discovery</h1>
        <p style="font-size:13px;color:var(--colorTextMuted);margin-bottom:32px">
          Map how your organization uses AI — and what it takes to secure it.
        </p>
        <button id="login-btn" style="width:100%;padding:12px 24px;border-radius:8px;font-size:14px;font-weight:600;cursor:pointer;border:none;background:var(--colorPrimary);color:#fff">
          Sign in with Okta
        </button>
      </div>
    </div>
  `;

  document.getElementById('login-btn')?.addEventListener('click', login);
}

function renderSetupWizard(container: HTMLElement): void {
  container.innerHTML = `
    <div style="display:flex;align-items:flex-start;justify-content:center;height:100vh;background:var(--colorBg)">
      <div class="modal-box" style="max-width:480px;margin-top:15vh">
        <h2 style="font-size:18px;font-weight:700;color:var(--colorTextHeading);margin-bottom:4px">Initial Setup</h2>
        <p style="font-size:12px;color:var(--colorTextMuted);margin-bottom:24px">
          Configure Okta OIDC to enable authentication. You'll need your Okta application credentials.
        </p>
        <div id="setup-error" style="display:none;padding:8px 12px;border-radius:6px;font-size:12px;background:rgba(239,68,68,.1);color:var(--colorError);margin-bottom:16px"></div>
        <form id="setup-form" style="display:flex;flex-direction:column;gap:12px">
          <div>
            <label style="font-size:11px;text-transform:uppercase;letter-spacing:.1em;color:var(--colorTextLight);display:block;margin-bottom:4px">Okta Issuer URL</label>
            <input id="setup-issuer" type="url" placeholder="https://your-org.okta.com/oauth2/default" required
              style="width:100%;padding:8px 12px;border:1px solid var(--colorBorderInput);border-radius:6px;font-size:13px;background:var(--colorBgInput);color:var(--colorText);box-sizing:border-box">
          </div>
          <div>
            <label style="font-size:11px;text-transform:uppercase;letter-spacing:.1em;color:var(--colorTextLight);display:block;margin-bottom:4px">Client ID</label>
            <input id="setup-client-id" type="text" placeholder="0oaxxxxxxxxxx" required
              style="width:100%;padding:8px 12px;border:1px solid var(--colorBorderInput);border-radius:6px;font-size:13px;background:var(--colorBgInput);color:var(--colorText);box-sizing:border-box">
          </div>
          <div>
            <label style="font-size:11px;text-transform:uppercase;letter-spacing:.1em;color:var(--colorTextLight);display:block;margin-bottom:4px">Client Secret</label>
            <input id="setup-client-secret" type="password" placeholder="Client secret" required
              style="width:100%;padding:8px 12px;border:1px solid var(--colorBorderInput);border-radius:6px;font-size:13px;background:var(--colorBgInput);color:var(--colorText);box-sizing:border-box">
          </div>
          <div>
            <label style="font-size:11px;text-transform:uppercase;letter-spacing:.1em;color:var(--colorTextLight);display:block;margin-bottom:4px">Redirect URI</label>
            <input id="setup-redirect-uri" type="url" required
              style="width:100%;padding:8px 12px;border:1px solid var(--colorBorderInput);border-radius:6px;font-size:13px;background:var(--colorBgInput);color:var(--colorText);box-sizing:border-box">
          </div>
          <button type="submit" id="setup-submit" style="margin-top:8px;padding:12px;border-radius:8px;font-size:14px;font-weight:600;cursor:pointer;border:none;background:var(--colorPrimary);color:#fff">
            Validate & Save
          </button>
        </form>
      </div>
    </div>
  `;

  // Pre-fill redirect URI
  const redirectInput = document.getElementById('setup-redirect-uri') as HTMLInputElement;
  if (redirectInput) {
    redirectInput.value = `${window.location.origin}/auth/callback`;
  }

  document.getElementById('setup-form')?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const errorEl = document.getElementById('setup-error')!;
    const submitBtn = document.getElementById('setup-submit') as HTMLButtonElement;

    errorEl.style.display = 'none';
    submitBtn.disabled = true;
    submitBtn.textContent = 'Validating...';

    try {
      await authService.setup({
        issuer: (document.getElementById('setup-issuer') as HTMLInputElement).value,
        client_id: (document.getElementById('setup-client-id') as HTMLInputElement).value,
        client_secret: (document.getElementById('setup-client-secret') as HTMLInputElement).value,
        redirect_uri: (document.getElementById('setup-redirect-uri') as HTMLInputElement).value,
      });

      // Reload to show login screen
      window.location.reload();
    } catch (err: any) {
      errorEl.textContent = err.message || 'Setup failed';
      errorEl.style.display = 'block';
      submitBtn.disabled = false;
      submitBtn.textContent = 'Validate & Save';
    }
  });
}
